// /src/components/core/HeadersAndFooters/FooterButtonsForm.jsx
// Footer buttons overlay for form/edit pages
// Used by: AddEditArticle, AddEditGroup, AddEditChat, AddEditEvent, MyProfile
// Buttons: Save as Draft, Cancel, Create and Exit

import { useState, useEffect } from 'react';
import StandardButton from '../StandardButton';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useTranslation } from '../../../i18n/useTranslation';

/**
 * FooterButtonsForm Component
 *
 * Provides standard form action buttons in the footer.
 * All buttons are conditional based on props passed.
 *
 * Props:
 * @param {Function} onSaveDraft - Handler for Save as Draft button
 * @param {Function} onCancel - Handler for Cancel button
 * @param {Function} onCreateAndExit - Handler for Create and Exit button
 * @param {boolean} isEditMode - If true, shows "Save and Exit" instead of "Create and Exit"
 */
function FooterButtonsForm({ onSaveDraft, onCancel, onCreateAndExit, isEditMode = false }) {
  const { isMobile } = useMediaQuery();
  const { t } = useTranslation('common');
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const checkScroll = () => {
      // Check if page is scrollable and scrolled down
      const isScrollable = document.documentElement.scrollHeight > window.innerHeight;
      const isScrolledDown = window.scrollY > 100;
      setShowScrollTop(isScrollable && isScrolledDown);
    };

    checkScroll();
    window.addEventListener('scroll', checkScroll);
    window.addEventListener('resize', checkScroll);

    return () => {
      window.removeEventListener('scroll', checkScroll);
      window.removeEventListener('resize', checkScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '0 8px' : '0 40px'
    }}>

      {/* CENTER SECTION: Buttons grouped together */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isMobile ? '8px' : '20px'
      }}>

        {/* Save as Draft */}
        {onSaveDraft && (
          <StandardButton
            variant="utility"
            iconName="Save"
            label={t('articles:save_draft')}
            onClick={onSaveDraft}
          />
        )}

        {/* Cancel */}
        {onCancel && (
          <StandardButton
            variant="destructive"
            iconName="X"
            label={t('cancel')}
            onClick={onCancel}
            confirmBefore={false}
          />
        )}

        {/* Create and Exit / Save and Exit */}
        {onCreateAndExit && (
          <StandardButton
            variant="action"
            iconName={isEditMode ? 'Save' : 'Send'}
            label={isEditMode ? t('articles:save_and_exit') : t('groups:create_and_exit')}
            onClick={onCreateAndExit}
          />
        )}

      </div>

      {/* Go To Top button - appears on right when scrolled (positioned to avoid Please Help button) - hidden on mobile */}
      {showScrollTop && !isMobile && (
        <div style={{
          position: 'absolute',
          right: '150px'
        }}>
          <StandardButton
            variant="action"
            iconName="ArrowUp"
            label={t('ui:go_to_top')}
            onClick={scrollToTop}
          />
        </div>
      )}

    </div>
  );
}

export default FooterButtonsForm;
